import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const LAST_UPDATED = 'March 24, 2026';
const CONTACT_EMAIL = 'submit@scoremyoffer.com';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      {/* Top bar */}
      <header className="legal-topbar">
        <Link to="/" className="legal-brand">
          <div className="legal-brand-icon">★</div>
          <span className="legal-brand-name">Score My Offer</span>
        </Link>
        <Link to="/signup" className="legal-back-link">
          ← Back to Sign Up
        </Link>
      </header>

      {/* Content */}
      <main className="legal-content">
        <div className="legal-hero">
          <div className="legal-badge">🔒 Privacy</div>
          <h1>Privacy Policy</h1>
          <div className="legal-meta">
            <span className="legal-meta-item">
              <span className="legal-meta-icon">📅</span>
              Last updated: {LAST_UPDATED}
            </span>
            <span className="legal-meta-item">
              <span className="legal-meta-icon">🏢</span>
              Score My Offer
            </span>
          </div>
        </div>

        {/* Table of Contents */}
        <nav className="legal-toc">
          <h3>Contents</h3>
          <ol>
            <li><a href="#overview">Overview</a></li>
            <li><a href="#information-collected">Information We Collect</a></li>
            <li><a href="#how-we-use">How We Use Your Information</a></li>
            <li><a href="#ai-third-parties">AI Processing &amp; Third Parties</a></li>
            <li><a href="#sharing">Information Sharing</a></li>
            <li><a href="#data-security">Data Security</a></li>
            <li><a href="#data-retention">Data Retention</a></li>
            <li><a href="#your-rights">Your Rights &amp; Choices</a></li>
            <li><a href="#cookies">Cookies &amp; Tracking</a></li>
            <li><a href="#children">Children's Privacy</a></li>
            <li><a href="#changes">Changes to This Policy</a></li>
            <li><a href="#contact">Contact Us</a></li>
          </ol>
        </nav>

        {/* Sections */}
        <section className="legal-section" id="overview">
          <h2><span className="legal-section-num">1</span> Overview</h2>
          <p>
            Score My Offer ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our real estate offer management platform at scoremyoffer.com (the "Platform").
          </p>
          <p>
            By using the Platform, you consent to the practices described in this Privacy Policy. If you do not agree, please discontinue use of the Platform.
          </p>
          <div className="legal-highlight">
            <p>
              <strong>Note:</strong> Score My Offer is designed for licensed real estate professionals. Documents you upload may contain personal information about third parties (buyers, borrowers). You are responsible for ensuring you have the legal right to share such information on the Platform.
            </p>
          </div>
        </section>

        <section className="legal-section" id="information-collected">
          <h2><span className="legal-section-num">2</span> Information We Collect</h2>

          <h3>Information You Provide Directly</h3>
          <ul>
            <li><strong>Account registration:</strong> First and last name, email address, phone number, user type (real estate agent or loan originator), and MLS or NMLS license number</li>
            <li><strong>Documents:</strong> Purchase agreements, pre-approval letters, MLS listing PDFs, and other files you upload to the Platform</li>
            <li><strong>Offer data:</strong> Property details, transaction terms, contingency information, and buyer information extracted from uploaded documents</li>
            <li><strong>Notes and ratings:</strong> Any notes or ratings you add about buyer's agents through the Platform</li>
            <li><strong>Communications:</strong> Messages or inquiries you send to us via email</li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage data:</strong> Pages viewed, features used, timestamps, and interaction patterns within the Platform</li>
            <li><strong>Device information:</strong> Browser type, operating system, IP address, and device identifiers</li>
            <li><strong>Authentication events:</strong> Login times, phone verification events, and email verification status</li>
          </ul>

          <h3>Information from Third Parties</h3>
          <ul>
            <li><strong>NMLS registry:</strong> We query the Nationwide Multistate Licensing System (NMLS) to verify loan originator license numbers during registration</li>
            <li><strong>Firebase Authentication:</strong> Phone verification is processed through Google Firebase's phone authentication service</li>
          </ul>
        </section>

        <section className="legal-section" id="how-we-use">
          <h2><span className="legal-section-num">3</span> How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Create and manage your account, including verifying your identity and professional credentials</li>
            <li>Process and analyze uploaded offer documents to generate PTC Scores and extracted data summaries</li>
            <li>Provide offer comparison, listing management, and lender vetting features</li>
            <li>Send transactional emails including account registration confirmation, verification links, approval/rejection notifications, and offer submission alerts</li>
            <li>Verify phone numbers via SMS during the registration process</li>
            <li>Detect and prevent fraud, abuse, and unauthorized access</li>
            <li>Improve and develop the Platform's features and algorithms</li>
            <li>Comply with applicable laws and regulations</li>
          </ul>
          <p>
            We do not use your data for general marketing purposes or sell your information to third parties for advertising.
          </p>
        </section>

        <section className="legal-section" id="ai-third-parties">
          <h2><span className="legal-section-num">4</span> AI Processing &amp; Third Parties</h2>
          <h3>Artificial Intelligence Use</h3>
          <p>
            Document content (purchase agreements, pre-approval letters, listing PDFs) is transmitted to AI model for extraction and analysis. By uploading documents to the Platform, you consent to this processing.
          </p>
          <h3>Google Firebase &amp; Google Cloud</h3>
          <p>
            The Platform is built on Google Cloud Platform, which provide authentication, database, file storage, and server-side compute services. Data is stored in Google Cloud Storage. Google's{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> applies to these services.
          </p>
          <h3>NMLS Consumer Access</h3>
          <p>
            We query publicly available NMLS records to verify loan originator credentials. No personal data is sent to NMLS beyond the license number provided.
          </p>
          <h3>reCAPTCHA</h3>
          <p>
            We use Google reCAPTCHA during registration to prevent automated abuse. reCAPTCHA collects hardware and software information and sends this to Google for analysis. Google's{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
          </p>
        </section>

        <section className="legal-section" id="sharing">
          <h2><span className="legal-section-num">5</span> Information Sharing</h2>
          <p>We do not sell, rent, or trade your personal information. We may share information in the following limited circumstances:</p>
          <ul>
            <li><strong>Within the Platform:</strong> Listing agents can view offer data submitted to their listings. Buyer's agent information associated with a submitted offer is visible to the listing agent who owns that listing.</li>
            <li><strong>Service providers:</strong> We share data with trusted third-party providers (Anthropic, Google Firebase) solely to operate the Platform, as described in Section 4.</li>
            <li><strong>Legal requirements:</strong> We may disclose information if required by law, court order, or governmental authority, or to protect the rights, property, or safety of Score My Offer, our users, or the public.</li>
            <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of that transaction.</li>
          </ul>
        </section>

        <section className="legal-section" id="data-security">
          <h2><span className="legal-section-num">6</span> Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your information, including:
          </p>
          <ul>
            <li>Encryption in transit (TLS/HTTPS) for all data transmitted between your browser and our servers</li>
            <li>Firebase Security Rules to enforce access controls on Firestore collections and Storage files</li>
            <li>Role-based access controls: users can only access data they are authorized to see</li>
            <li>Phone number verification during registration to reduce fraudulent accounts</li>
            <li>Admin-reviewed account approvals before access is granted</li>
          </ul>
          <p>
            No method of electronic storage or transmission is 100% secure. While we strive to protect your data, we cannot guarantee absolute security. If you suspect a security incident, please notify us immediately at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section className="legal-section" id="data-retention">
          <h2><span className="legal-section-num">7</span> Data Retention</h2>
          <p>
            We retain your account information and associated data for as long as your account is active or as needed to provide services. Specifically:
          </p>
          <ul>
            <li><strong>Account profiles:</strong> Retained for the life of your account plus a reasonable period after closure for audit and legal compliance purposes</li>
            <li><strong>Uploaded documents:</strong> Retained while your account is active; you may request deletion at any time</li>
            <li><strong>Offer data and extracted information:</strong> Retained for the life of the associated listing or account, whichever is longer</li>
            <li><strong>Agent notes and ratings:</strong> Retained with your account; deleted upon account closure or upon your request</li>
          </ul>
          <p>
            To request deletion of your data, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section className="legal-section" id="your-rights">
          <h2><span className="legal-section-num">8</span> Your Rights &amp; Choices</h2>
          <p>Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements</li>
            <li><strong>Portability:</strong> Request your data in a portable format where technically feasible</li>
            <li><strong>Objection:</strong> Object to certain processing activities</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within a reasonable timeframe.
          </p>
          <h3>California Residents</h3>
          <p>
            California residents may have additional rights under the California Consumer Privacy Act (CCPA), including the right to know, right to delete, and right to opt out of sale of personal information. We do not sell personal information. To submit a CCPA request, contact us at the email above.
          </p>
        </section>

        <section className="legal-section" id="cookies">
          <h2><span className="legal-section-num">9</span> Cookies &amp; Tracking</h2>
          <p>
            The Platform uses cookies and similar technologies to maintain your authenticated session, remember your preferences, and ensure secure access. Specifically:
          </p>
          <ul>
            <li><strong>Session cookies:</strong> Used by Firebase Authentication to maintain your logged-in state</li>
            <li><strong>reCAPTCHA:</strong> Google reCAPTCHA uses cookies and fingerprinting to distinguish humans from bots</li>
          </ul>
          <p>
            We do not use third-party advertising cookies or track you across other websites. You can configure your browser to refuse cookies, but this may impair Platform functionality, including the ability to stay logged in.
          </p>
        </section>

        <section className="legal-section" id="children">
          <h2><span className="legal-section-num">10</span> Children's Privacy</h2>
          <p>
            The Platform is intended exclusively for adults aged 18 and older who are licensed real estate professionals. We do not knowingly collect personal information from anyone under 18. If you believe a minor has provided us with personal information, please contact us and we will promptly delete it.
          </p>
        </section>

        <section className="legal-section" id="changes">
          <h2><span className="legal-section-num">11</span> Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will update the "Last updated" date at the top of this page and, where appropriate, notify registered users via email.
          </p>
          <p>
            Your continued use of the Platform after any changes constitutes your acceptance of the updated Privacy Policy.
          </p>
        </section>

        <section className="legal-section" id="contact">
          <h2><span className="legal-section-num">12</span> Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="legal-contact-box">
            <p><strong>Score My Offer — Privacy</strong></p>
            <p>Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
            <p>Website: <a href="https://scoremyoffer.com">scoremyoffer.com</a></p>
          </div>
        </section>
      </main>

      <footer className="legal-footer">
        © {new Date().getFullYear()} Score My Offer. All rights reserved.
        {' · '}
        <Link to="/terms">Terms &amp; Conditions</Link>
        {' · '}
        <Link to="/privacy">Privacy Policy</Link>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
